import { useState } from 'react';
import MercariDashboardService from './mercariDashboardService';
import { useSelector } from 'react-redux';

function useMercariDashboard() {
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [priceNegoDetail, setPriceNegoDetail] = useState(null);
  const [error, setError] = useState(null);
  const [purchaseDetail, setPurchaseDetail] = useState(null);

  const getDashboardItems = async (shop) => {
    setIsLoadingDashboard(true);
    setDashboardError(null);
    try {
      const response = await MercariDashboardService.getDashboardItems(shop);
      if (response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch dashboard items');
    } catch (err) {
      console.error('Error in getDashboardItems:', err);
      setDashboardError(err.response?.data?.message || 'An error occurred while fetching dashboard items');
      // Rethrow: returning [] here made a failed load look like an empty board, and
      // the caller's "load failed" banner could never show (QA 602).
      throw err;
    } finally {
      setIsLoadingDashboard(false);
    }
  };

  // `source` ('paypay') routes the SAME endpoint to the shared negotiation
  // table on the backend; omitted → the Mercari table, exactly as before.
  const takeNewPriceNegotiationCase = async (negotiationId, forceTake = false, source = null) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.takeNewPriceNegotiationCase({
        negotiationId,
        forceTake,
        ...(source ? { source } : {})
      });

      if (response.data.success) {
        setPriceNegoDetail(response.data.data.caseDetails);
        return {
          success: true,
          data: response.data.data.caseDetails
        };
      } else if (response.data.caseStatus === 'already_taken') {
        return {
          success: false,
          caseStatus: 'already_taken',
          currentAdmin: response.data.currentAdmin
        };
      }
    } catch (err) {
      console.error('Error in takeNewPriceNegotiationCase:', err);
      setError(err.response?.data?.message || 'An error occurred while taking the case');
      return {
        success: false,
        error: err.response?.data?.message || 'An error occurred while taking the case'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const releasePriceNegotiationCase = async (negotiationId, source = null) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.releasePriceNegotiationCase(negotiationId, source);
      return response.data?.success;
    } catch (err) {
      console.error('Error in releasePriceNegotiationCase:', err);
      setError(err.response?.data?.message || 'An error occurred while canceling the case');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const completePriceNegotiationCase = async (formData) => {
    try {
      const response = await MercariDashboardService.completePriceNegotiationCase(formData);
      return response.data.success;
    } catch (error) {
      console.error('Error completing price negotiation case:', error);
      throw error;
    }
  };

  // `source` routes the SAME endpoint to the custom-link case handler on the
  // backend (source:'customlink'); omitted → the mercari purchase path.
  const takeNewPurchaseCase = async (purchaseId, forceTake = false, source = null) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.takeNewPurchaseCase({
        purchaseId,
        forceTake,
        ...(source ? { source } : {})
      });

      if (response.data.success) {
        console.log('takeNewPurchaseCase', response.data.data.caseDetails);
        setPurchaseDetail(response.data.data.caseDetails);
        return {
          success: true,
          data: response.data.data.caseDetails
        };
      } else if (response.data.caseStatus === 'already_taken') {
        return {
          success: false,
          caseStatus: 'already_taken',
          currentAdmin: response.data.currentAdmin
        };
      }
    } catch (err) {
      console.error('Error in takeNewPurchaseCase:', err);
      setError(err.response?.data?.message || 'An error occurred while taking the case');
      return {
        success: false,
        error: err.response?.data?.message || 'An error occurred while taking the case'
      };
    } finally {
      setIsLoading(false);
    }
  };

  const releasePurchaseCase = async (purchaseId, source = null) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.releasePurchaseCase(purchaseId, source);
      return response.data?.success;
    } catch (err) {
      console.error('Error in releasePurchaseCase:', err);
      setError(err.response?.data?.message || 'An error occurred while canceling the case');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const completePurchaseCase = async (formData) => {
    try {
      const response = await MercariDashboardService.completePurchaseCase(formData);
      // Full payload (success + data.netSettlement / data.refunds + any paymentUrl) so the
      // caller can surface the reconcile result (payment link to send / refund created).
      return response.data;
    } catch (error) {
      console.error('Error completing purchase case:', error);
      throw error;
    }
  };

  // ─── ส่งตรวจ (Anshin appraisal) ────────────────────────────────────────────
  // Same claim-then-work shape as the other case types: the backend refuses a
  // result or a defer unless this admin is holding the case.
  //
  // The 2nd/3rd args exist only to match the (id, forceTake, source) signature
  // handleCaseTaking calls every take function with; an appraisal has one source.
  const takeAppraisalCase = async (orderId, forceTake = false) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.takeAppraisalCase({ orderId, forceTake });

      if (response.data.success) {
        // Already flat (no caseDetails wrapper) — the board spreads it onto the modal.
        return { success: true, data: response.data.data };
      }
      if (response.data.caseStatus === 'already_taken') {
        return {
          success: false,
          caseStatus: 'already_taken',
          currentAdmin: response.data.currentAdmin
        };
      }
      return { success: false };
    } catch (err) {
      console.error('Error in takeAppraisalCase:', err);
      setError(err.response?.data?.message || 'An error occurred while taking the case');
      return { success: false, error: err.response?.data?.message };
    } finally {
      setIsLoading(false);
    }
  };

  const releaseAppraisalCase = async (orderId) => {
    setIsLoading(true);
    try {
      const response = await MercariDashboardService.releaseAppraisalCase(orderId);
      return response.data?.success;
    } catch (err) {
      console.error('Error in releaseAppraisalCase:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const recordAppraisalResult = async ({ orderId, result, note }) => {
    const response = await MercariDashboardService.recordAppraisalResult({ orderId, result, note });
    return response.data;
  };

  // Read-only: what ไม่ผ่าน would do to the order and the money.
  const previewAppraisalFailure = async (orderId) => {
    const response = await MercariDashboardService.previewAppraisalFailure(orderId);
    return response.data;
  };

  const deferAppraisalCheck = async (orderId) => {
    const response = await MercariDashboardService.deferAppraisalCheck(orderId);
    return response.data;
  };

  // Re-find guest +Δ reconcile payment links (the confirm result dialog shows a link once).
  const getReconcileCollections = async (shop, status = 'pending') => {
    try {
      const response = await MercariDashboardService.getReconcileCollections(shop, status);
      return response.data?.data || [];
    } catch (err) {
      console.error('Error in getReconcileCollections:', err);
      setError(err.response?.data?.message || 'An error occurred while fetching reconcile collections');
      return [];
    }
  };

  return {
    isLoadingDashboard,
    dashboardError,
    isLoading,
    error,
    priceNegoDetail,
    purchaseDetail,
    getDashboardItems,
    takeNewPriceNegotiationCase,
    releasePriceNegotiationCase,
    completePriceNegotiationCase,
    takeNewPurchaseCase,
    releasePurchaseCase,
    completePurchaseCase,
    takeAppraisalCase,
    releaseAppraisalCase,
    recordAppraisalResult,
    previewAppraisalFailure,
    deferAppraisalCheck,
    getReconcileCollections,
  };
}

export default useMercariDashboard;
